import React, { useState, useRef, useEffect } from 'react'
import { Card, CardContent, CardHeader } from './ui/card'
import { Button } from './ui/button'
import { Badge } from './ui/badge'
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar'
import { buildSummaryMacroInfoV2, matchingAttrs } from '../utils/utils'
import { fetchMatchReason } from '../utils/hiringAgentApi'
import { 
  MapPin, 
  Briefcase, 
  Star, 
  Heart, 
  X, 
  FileText,
  Expand,
  Loader2,
  Building2,
  GraduationCap
} from 'lucide-react'

const CandidateTiles = ({ candidates, isLoading = false, totalMatches, macrosByCategories, icpRequirements, userId }) => {
  // Limit to top 20 candidates for performance
  const topCandidates = candidates.slice(0, 20)
  const [selectedCandidate, setSelectedCandidate] = useState(null)
  const [shortlistedCandidates, setShortlistedCandidates] = useState(new Set())
  const [rejectedCandidates, setRejectedCandidates] = useState(new Set())
  const [matchReasons, setMatchReasons] = useState(new Map()) // Store match reasons by candidateId
  const [loadingMatchReasons, setLoadingMatchReasons] = useState(new Set()) // Track which candidates are loading
  const searchCriteria = buildSummaryMacroInfoV2(icpRequirements, macrosByCategories);
  const handleShortlist = (candidateId, event) => {
    event.stopPropagation()
    const newShortlisted = new Set(shortlistedCandidates)
    if (newShortlisted.has(candidateId)) {
      newShortlisted.delete(candidateId)
    } else {
      newShortlisted.add(candidateId)
      // Remove from rejected if was rejected
      const newRejected = new Set(rejectedCandidates)
      newRejected.delete(candidateId)
      setRejectedCandidates(newRejected)
    }
    setShortlistedCandidates(newShortlisted)
  }

  const handleReject = (candidateId, event) => {
    event.stopPropagation()
    const newRejected = new Set(rejectedCandidates)
    if (newRejected.has(candidateId)) {
      newRejected.delete(candidateId)
    } else {
      newRejected.add(candidateId)
      // Remove from shortlisted if was shortlisted
      const newShortlisted = new Set(shortlistedCandidates)
      newShortlisted.delete(candidateId)
      setShortlistedCandidates(newShortlisted)
    }
    setRejectedCandidates(newRejected)
  }

  const getMatchScoreColor = (score) => {
    if (score >= 90) return 'text-green-600 bg-green-100'
    if (score >= 80) return 'text-blue-600 bg-blue-100'
    if (score >= 70) return 'text-yellow-600 bg-yellow-100'
    return 'text-gray-600 bg-gray-100'
  }

  const VirtualizedCandidateTile = ({ candidate, isExpanded = false }) => {
    const [isVisible, setIsVisible] = useState(false)
    const [hasBeenVisible, setHasBeenVisible] = useState(false)
    const ref = useRef()

    useEffect(() => {
      const element = ref.current
      if (!element) return

      const observer = new IntersectionObserver(
        ([entry]) => {
          const isIntersecting = entry.isIntersecting
          setIsVisible(isIntersecting)
          if (isIntersecting && !hasBeenVisible) {
            setHasBeenVisible(true)
          }
        },
        { 
          root: null,
          rootMargin: '200px', // Large margin for smooth loading
          threshold: 0
        }
      )

      observer.observe(element)

      return () => {
        observer.unobserve(element)
      }
    }, [hasBeenVisible])

    if (!isVisible && !hasBeenVisible) {
      return (
        <div 
          ref={ref}
          className="bg-gray-100 animate-pulse rounded-lg flex min-w-72 w-72 h-auto items-center justify-center"
        >
          <span className="text-gray-500 text-sm">Loading...</span>
        </div>
      )
    }

    return <CandidateTile ref={ref} candidate={candidate} isExpanded={isExpanded} />
  }

  const handleExpandCandidate = async (candidateId, event) => {
    event?.stopPropagation()
    
    const newSelectedCandidate = selectedCandidate === candidateId ? null : candidateId
    setSelectedCandidate(newSelectedCandidate)
    
    console.log('Expanding candidate:', candidateId, 'Selected:', newSelectedCandidate);
    console.log('Search criteria:', searchCriteria);
    console.log('User ID:', userId);
    
    // If expanding and we don't have match reason cached, fetch it
    if (newSelectedCandidate && !matchReasons.has(candidateId)) {
      console.log('Fetching match reason for candidate:', candidateId);
      const newLoadingSet = new Set(loadingMatchReasons)
      newLoadingSet.add(candidateId)
      setLoadingMatchReasons(newLoadingSet)
      
      try {
        const matchReasonData = await fetchMatchReason(candidateId, userId, searchCriteria)
        console.log('Match reason data received:', matchReasonData);
        const newMatchReasons = new Map(matchReasons)
        newMatchReasons.set(candidateId, matchReasonData)
        setMatchReasons(newMatchReasons)
      } catch (error) {
        console.error('Error fetching match reason for candidate:', candidateId, error)
        // Set a fallback message
        const newMatchReasons = new Map(matchReasons)
        newMatchReasons.set(candidateId, "Unable to load match analysis. Please try again later.")
        setMatchReasons(newMatchReasons)
      } finally {
        const newLoadingSet = new Set(loadingMatchReasons)
        newLoadingSet.delete(candidateId)
        setLoadingMatchReasons(newLoadingSet)
      }
    }
  }

  const CandidateTile = React.forwardRef(({ candidate, isExpanded = false }, ref) => {
    const isShortlisted = shortlistedCandidates.has(candidate.id);
    const isRejected = rejectedCandidates.has(candidate.id);
    const candidateMatchReason = matchReasons.get(candidate.id);
    const isLoadingMatchReason = loadingMatchReasons.has(candidate.id);
    return (
      <Card 
        ref={ref}
        className={`
          min-w-72 h-auto cursor-pointer transition-all duration-200 hover:shadow-md
          ${isShortlisted ? 'ring-2 ring-green-500 bg-green-50' : ''}
          ${isRejected ? 'ring-2 ring-red-500 bg-red-50 opacity-60' : ''}
          ${isExpanded ? 'min-w-80' : ''}
        `}
        onClick={(e) => handleExpandCandidate(candidate.id, e)}
      >
        <CardHeader className="pb-2 pt-4 px-4">
          <div className="flex items-start justify-between">
            <div className="flex items-center space-x-2">
              <Avatar className="h-10 w-10">
                <AvatarImage src={candidate.avatar} alt={candidate.name} />
                <AvatarFallback className="bg-blue-100 text-blue-600 font-medium text-sm">
                  {candidate.name.split(' ').map(n => n[0]).join('')}
                </AvatarFallback>
              </Avatar>
              <div className="min-w-0 flex-1">
                <h3 className="font-semibold text-gray-900 text-sm truncate">
                  {candidate.name}
                </h3>
                <p className="text-xs text-gray-600 font-medium truncate">{candidate.title}</p>
                {candidate.company && (
                  <p className="text-xs text-gray-500 truncate flex items-center">
                    <Building2 className="h-3 w-3 mr-1" />
                    {candidate.company}
                  </p>
                )}
              </div>
            </div>
            
            <Badge className={`${getMatchScoreColor(candidate.matchScore)} border-0 text-xs`}>
              <Star className="h-2 w-2 mr-1 fill-current" />
              {candidate.matchScore}%
            </Badge>
          </div>
        </CardHeader>
        
        <CardContent className="pt-0 px-4 pb-3">
          {/* Basic Info */}
          <div className="space-y-1 mb-3">
            <div className="flex items-center text-xs text-gray-600">
              <Briefcase className="h-3 w-3 mr-1" />
              <span className="truncate">{candidate.experience} • {candidate.industry}</span>
            </div>
            <div className="flex items-center text-xs text-gray-600">
              <MapPin className="h-3 w-3 mr-1" />
              <span className="truncate">{candidate.location}</span>
            </div>
            {candidate.education && (
              <div className="flex items-center text-xs text-gray-600">
                <GraduationCap className="h-3 w-3 mr-1" />
                <span className="truncate">{candidate.education}</span>
              </div>
            )}
          </div>

          {/* Skills */}
          {candidate.skills && candidate.skills.length > 0 && (
            <div className="mb-3">
              <div className="flex flex-wrap gap-1">
                {candidate.skills.slice(0, isExpanded ? candidate.skills.length : 2).map((skill, index) => (
                  <Badge key={index} variant="outline" className="text-xs py-0 px-1">
                    {skill}
                  </Badge>
                ))}
                {!isExpanded && candidate.skills.length > 2 && (
                  <Badge variant="outline" className="text-xs text-gray-500 py-0 px-1">
                    +{candidate.skills.length - 2}
                  </Badge>
                )}
              </div>
            </div>
          )}

          {/* Expanded Details */}
          {isExpanded && selectedCandidate === candidate.id && (
            <div className="space-y-3 border-t pt-3 mt-3">
              <div>
                <p className="text-sm font-medium text-gray-700 mb-1">Match Reason</p>
                {isLoadingMatchReason ? (
                  <div className="flex items-center space-x-2 text-sm text-gray-600">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>Loading match reason...</span>
                  </div>
                ) : candidateMatchReason ? (
                  <div className="text-sm text-gray-600">
                    <div className="whitespace-pre-line max-w-96">
                      <ul className='list-disc ml-5 space-y-1'>
                        {candidateMatchReason.split('\n').map((line, index) => (
                          <li key={index} dangerouslySetInnerHTML={{ __html: line }} />
                        ))}
                      </ul>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-gray-600">
                    Click to expand and see detailed match analysis for this candidate.
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex items-center justify-between">
            <div className="flex space-x-1">
              {candidate.linkedin && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={(event) => {
                    event.stopPropagation()
                    window.open(candidate.linkedin, '_blank')
                  }}
                  className="h-7 px-2 text-xs"
                >
                  <FileText className="h-3 w-3 mr-1" />
                  Profile
                </Button>
              )}
              
              <Button
                size="sm"
                variant="outline"
                onClick={(event) => handleExpandCandidate(candidate.id, event)}
                className="h-7 px-2 text-xs"
              >
                <Expand className="h-3 w-3 mr-1" />
                {selectedCandidate === candidate.id ? 'Less' : 'More'}
              </Button>
            </div>
            
            <div className="flex space-x-1">
              <Button
                size="sm"
                variant={isShortlisted ? "default" : "outline"}
                onClick={(e) => handleShortlist(candidate.id, e)}
                className={`h-7 w-7 p-0 ${isShortlisted ? "bg-green-600 hover:bg-green-700" : "hover:bg-green-50 hover:text-green-600 hover:border-green-300"}`}
              >
                <Heart className={`h-3 w-3 ${isShortlisted ? 'fill-current' : ''}`} />
              </Button>
              
              <Button
                size="sm"
                variant={isRejected ? "destructive" : "outline"}
                onClick={(e) => handleReject(candidate.id, e)}
                className={`h-7 w-7 p-0 ${!isRejected ? "hover:bg-red-50 hover:text-red-600 hover:border-red-300" : ""}`}
              >
                <X className="h-3 w-3" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    )
  })

  // Loading state component
  const LoadingTile = () => (
    <Card className="min-w-72 h-auto">
      <CardHeader className="pb-2 pt-4 px-4">
        <div className="flex items-start justify-between">
          <div className="flex items-center space-x-2">
            <div className="h-10 w-10 bg-gray-200 rounded-full animate-pulse"></div>
            <div className="min-w-0 flex-1 space-y-1">
              <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
              <div className="h-3 bg-gray-200 rounded animate-pulse w-3/4"></div>
            </div>
          </div>
          <div className="h-6 w-12 bg-gray-200 rounded animate-pulse"></div>
        </div>
      </CardHeader>
      <CardContent className="pt-0 px-4 pb-3">
        <div className="space-y-2">
          <div className="h-3 bg-gray-200 rounded animate-pulse"></div>
          <div className="h-3 bg-gray-200 rounded animate-pulse w-4/5"></div>
          <div className="flex space-x-1 mt-2">
            <div className="h-5 w-12 bg-gray-200 rounded animate-pulse"></div>
            <div className="h-5 w-16 bg-gray-200 rounded animate-pulse"></div>
          </div>
        </div>
      </CardContent>
    </Card>
  )

  return (
    <div className="p-4">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Finding Candidates...
              </>
            ) : (
`Top 20 Candidate Matches ${totalMatches ? `(${totalMatches} total)` : ''}`
            )}
          </h3>
          <p className="text-sm text-gray-600">
            {isLoading ? 'Searching our database for the best matches' : 'Scroll horizontally to view all candidates'}
          </p>
        </div>
        
        {!isLoading && shortlistedCandidates.size > 0 && (
          <Badge variant="secondary" className="bg-green-100 text-green-800">
            {shortlistedCandidates.size} shortlisted
          </Badge>
        )}
      </div>

      {/* Horizontal scrolling container */}
      <div className="relative">
        <div className="w-full overflow-x-auto pb-4" style={{ scrollbarWidth: 'thin' }}>
          <div className="flex space-x-4 pl-4 pr-4 min-w-max">
            {isLoading ? (
              // Show loading tiles
              Array.from({ length: 3 }).map((_, index) => (
                <LoadingTile key={`loading-${index}`} />
              ))
            ) : (
              // Show top 20 candidates with virtualization
              topCandidates.map((candidate) => (
                <VirtualizedCandidateTile 
                  key={candidate.id} 
                  candidate={candidate}
                  isExpanded={selectedCandidate === candidate.id}
                />
              ))
            )}
          </div>
        </div>
      </div>

      {/* Summary stats */}
      <div className="mt-4 flex items-center space-x-6 text-sm text-gray-600">
        <span>
          <span className="font-medium">{topCandidates.filter(c => c.matchScore >= 90).length}</span> excellent matches
        </span>
        <span>
          <span className="font-medium">{topCandidates.filter(c => c.matchScore >= 80 && c.matchScore < 90).length}</span> good matches
        </span>
        <span>
          <span className="font-medium">{shortlistedCandidates.size}</span> shortlisted
        </span>
        <span>
          <span className="font-medium">{rejectedCandidates.size}</span> rejected
        </span>
      </div>
    </div>
  )
}

export default CandidateTiles